import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ChevronDown, ChevronRight, History } from 'lucide-react';
import { Link } from 'react-router-dom';
import { KIND_LABELS, captureFilingState, filedItem, latestClassification } from '@cerebro-diem/core';
import { recentCaptures } from '../../services/captures';
import { useContainers } from '../../services/containers';
import { errorMessage } from '../../services/errors';
import { queryKeys } from '../../services/queries';
import { STATE_TONES, stateLabel } from '../home/captureLabels';

const LIMIT = 20;

/**
 * The last captures as they were made, and what became of each: for finding
 * out what happened to something said or typed, not for everyday use. A
 * capture is normally only its task or note; the capture itself stays stored
 * and shows here. Collapsed until opened, and read only then.
 */
export default function RecentCaptures() {
  const [open, setOpen] = useState(false);
  const recent = useQuery({ queryKey: [...queryKeys.captures, 'recent', LIMIT], queryFn: () => recentCaptures(LIMIT), enabled: open });
  const containers = useContainers().data ?? [];
  const now = new Date();

  return (
    <section className="bg-surface rounded-xl p-4 sm:p-6 shadow-sm">
      <h2 className="text-lg font-semibold">
        <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left">
          {open ? <ChevronDown className="w-4 h-4 text-on-surface-variant" /> : <ChevronRight className="w-4 h-4 text-on-surface-variant" />}
          <History className="w-5 h-5 text-primary" />
          Recent captures
        </button>
      </h2>
      {open && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-on-surface-variant">
            What you said or typed, as it arrived, and what became of it. For troubleshooting: day to day, a capture is the task or note it became.
          </p>
          {recent.isPending && <p className="text-sm text-on-surface-variant">Loading…</p>}
          {recent.isError && <p className="text-sm text-error">Could not load recent captures: {errorMessage(recent.error)}</p>}
          {recent.data && recent.data.captures.length === 0 && <p className="text-sm text-on-surface-variant">No captures yet.</p>}
          {recent.data && recent.data.captures.length > 0 && (
            <ul className="space-y-2">
              {recent.data.captures.map((capture) => {
                const classification = latestClassification(capture);
                const state = captureFilingState(capture, classification, now);
                const item = filedItem(classification);
                const place = item ? recent.data.places[item.id] : undefined;
                const container = place ? containers.find((c) => c.kind === place.kind && c.id === place.id) : undefined;
                const where = item
                  ? `Filed as ${KIND_LABELS[item.kind]}${container ? ` in ${container.name}` : ''}`
                  : stateLabel({ capture, classification, state }, containers);
                const body = (
                  <>
                    <p className="text-sm whitespace-pre-wrap break-words line-clamp-2">{capture.raw_text}</p>
                    <p className="text-xs flex flex-wrap gap-x-2">
                      <span className={`min-w-0 ${STATE_TONES[state]}`}>Saved · {where}</span>
                      <span className="text-on-surface-variant">{format(new Date(capture.created_at), 'MMM d, h:mm a')}</span>
                    </p>
                  </>
                );
                return (
                  <li key={capture.id}>
                    {item ? (
                      <Link
                        to={item.kind === 'task' ? `/tasks/${item.id}` : `/notes/${item.id}`}
                        className="block p-3 rounded-xl border border-outline/20 space-y-1 hover:bg-surface-variant/30"
                      >
                        {body}
                      </Link>
                    ) : (
                      <div className="p-3 rounded-xl border border-outline/20 space-y-1">
                        {body}
                        <p className="text-xs text-on-surface-variant">File it from the Inbox on Today.</p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
